/**
 * Pre-Sailing Familiarisation - WBC3 Appendix 8, Section 9 Compliance
 * Safety briefing required before commencing any voyage
 *
 * DISTINCTION: This is pre-departure briefing for ALL persons onboard
 * vs Section 7.2 which is detailed crew familiarisation training
 */

import { parseCategory } from '../../utils/categoryHelpers';

// Helper for vessel type normalization
const normalizeVesselType = (type) => {
  return (type || '').toLowerCase().trim();
};

// Pre-Sailing Familiarisation items per WBC3 Appendix 8, Section 9
export const PRE_SAILING_FAMILIARISATION_ITEMS = [
  {
    id: 'lifejacket_location_presail',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Location of Lifejackets',
    trigger: 'Always',
    reference: 'Pre-departure safety briefing',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Ensure all persons know where to find and how to don them.'
  },
  {
    id: 'fire_extinguisher_location_presail',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Location of Fire Extinguishers',
    trigger: 'Always',
    reference: 'Pre-departure safety briefing',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Ensure all onboard can identify where extinguishers are located.'
  },
  {
    id: 'muster_station_awareness',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Muster Points and Escape Routes',
    trigger: 'Always',
    reference: 'Pre-departure safety briefing',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Confirm all crew and passengers are briefed on emergency mustering and escape.'
  },
  {
    id: 'how_to_raise_alarm',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'How to Raise the Alarm',
    trigger: 'Always',
    reference: 'Familiarisation briefing before sailing',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Show how to raise the alarm — e.g. alarm button, shouting, or using VHF Channel 16 if instructed.'
  },

  // CONDITIONAL ITEMS - Additional briefing for enhanced operations
  {
    id: 'liferaft_location_presail',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Life Raft Location and Access',
    trigger: (vessel) => vessel.liferafts_fitted === true,
    reference: 'Enhanced pre-departure briefing',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No life rafts fitted', 'Not required for vessel category'],
    tooltip: 'Brief all persons on liferaft location and access if fitted.'
  },
  {
    id: 'mob_recovery_awareness',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Man Overboard Recovery Basics',
    trigger: (vessel) => vessel.rescue_boat_fitted === true || normalizeVesselType(vessel.type) === 'rib' || normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Enhanced pre-departure briefing',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No recovery equipment', 'Shore-based operations only'],
    tooltip: 'Basic MOB awareness for vessels with recovery capability.'
  },
  {
    id: 'pyrotechnics_awareness',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Pyrotechnics Location and Purpose',
    trigger: (vessel) => {
      // parseCategory handles 'cat3', 3, '3', 'CAT3', 'IWW' formats
      const cat = parseCategory(vessel.category);
      return cat <= 2;
    },
    reference: 'Enhanced pre-departure briefing',
    tag: 'Conditional',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No pyrotechnics carried', 'Not required for vessel category'],
    tooltip: 'Brief all persons on pyrotechnic signals if carried.'
  },
  {
    id: 'navigation_lights_awareness',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Navigation and Emergency Light Switches',
    trigger: (vessel) => {
      // parseCategory handles 'cat3', 3, '3', 'CAT3', 'IWW' formats
      const cat = parseCategory(vessel.category);
      return vessel.enclosed_accommodation_present === true || cat <= 3;
    },
    reference: 'Enhanced pre-departure briefing',
    tag: 'Conditional',
    priority: 'medium',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['Daylight operations only', 'No enclosed accommodation'],
    tooltip: 'Location of navigation and emergency lighting controls.'
  },
  {
    id: 'emergency_engine_controls',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency Engine Start/Stop Basics',
    trigger: (vessel) => {
      // parseCategory handles 'cat3', 3, '3', 'CAT3', 'IWW' formats
      const cat = parseCategory(vessel.category);
      return vessel.emergency_steering === true || normalizeVesselType(vessel.type) === 'tug' || cat <= 2;
    },
    reference: 'Enhanced pre-departure briefing',
    tag: 'Conditional',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No emergency procedures required', 'Single engine operations'],
    tooltip: 'Basic emergency engine control awareness for backup crew.'
  }
];

/**
 * Generate Pre-Sailing Familiarisation checklist
 * WBC3-compliant pre-departure safety briefing per Appendix 8, Section 9
 * 
 * This is distinct from Section 7.2 crew familiarisation - this is immediate
 * safety awareness for ALL persons before vessel departure
 */
export function generatePreSailingFamiliarisationChecklist(vessel, crew) {
  const items = [];
  
  PRE_SAILING_FAMILIARISATION_ITEMS.forEach(item => {
    // Check trigger condition
    let includeItem = false;
    
    if (item.trigger === 'Always') {
      includeItem = true;
    } else if (typeof item.trigger === 'function') {
      includeItem = item.trigger(vessel);
    }
    
    if (includeItem) {
      // Add rank visibility - pre-sailing applies to ALL persons
      const processedItem = {
        ...item,
        rankVisibility: {
          'Master': 'full_operational',
          'Engineer': 'full_operational',
          'Crew': 'full_operational', 
          'Crew Member': 'full_operational',
          'Supernumerary': 'general_awareness_supervised',
          'Passenger': 'general_awareness_supervised', // Pre-sailing includes passengers
          'Contractor': 'general_awareness_supervised'
        }
      };
      
      items.push(processedItem);
    }
  });
  
  return {
    section: 'pre_sailing_familiarisation',
    title: 'Pre-Sailing Familiarisation',
    items: items,
    completionRequired: true,
    blocksOperations: true,
    auditTrail: true,
    targetCompletionMinutes: 30,
    description: 'Essential safety briefing required before vessel departure per WBC3 Appendix 8, Section 9'
  };
}

// Pre-sailing completion rules per WBC3 Appendix 8, Section 9
export const PRE_SAILING_RULES = {
  // Must be completed before vessel departure
  required_before_departure: true,
  
  // Can be completed quickly (target: 15-30 minutes)
  target_completion_minutes: 30,
  
  // Blocks vessel operations if not completed
  blocks_operations: true,
  
  // Applies to ALL persons onboard (crew, passengers, contractors)
  applies_to_all_persons: true,
  
  // Integration with other systems
  integration: {
    vessel_readiness: 'Required for vessel departure clearance',
    drill_participation: 'Minimum requirement before emergency drill participation',
    equipment_access: 'Enables basic safety equipment identification'
  },
  
  // Audit requirements
  audit: {
    master_sign_off_required: true,
    completion_timestamp: true,
    crew_acknowledgment: true,
    passenger_acknowledgment: true // If passengers carried
  }
};